// The feedback list pane (docs/06-ux-rules.md "List"; 11-chassis-plan.md §2
// PR 04). Header = `SearchHeader`, body = rows (or a loading/error/empty
// state), footer = `TablePaginationFooter`. In look-back mode, the entire
// normal composition is hidden (not unmounted, so its scroll position
// survives) behind `LookbackBackHeader` (docs/06-ux-rules.md "Look-back").
//
// Selection/animation state is owned by the container (`FeedbackInbox/
// index.tsx`) — this component is presentational plus the one piece of
// local state that's genuinely this pane's own concern: which row is the
// roving keyboard tab stop (`useRovingListNav`).
import { Button } from '@thanx/uikit/button'
import { Text } from '@thanx/uikit/text'
import type {
  FeedbackInboxItem,
  InboxKind,
  InboxStatus,
  RatingFilter,
} from 'api/feedbackInbox.types'
import TablePaginationFooter, {
  type PageSize,
} from 'components/TablePaginationFooter'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React, { useEffect, useRef } from 'react'
import { useStyletron } from 'styletron-react'
import { useRovingListNav } from '../useRovingListNav'
import { emptyStateCause } from './emptyStateCause'
import InboxListRow from './InboxListRow'
import LookbackBackHeader from './LookbackBackHeader'
import SearchHeader from './SearchHeader'
import type { ResolveAnimationPhase } from './useResolveAnimation'

const t = buildTranslate('feedback_inbox.list')

const SKELETON_ROW_COUNT = 5

export interface InboxListProps {
  status: InboxStatus
  kind: InboxKind
  rating: RatingFilter | null
  q: string
  onSearchChange: (q: string) => void
  locationIds: number[]
  items: FeedbackInboxItem[]
  totalCount: number
  totalPages: number
  page: number
  pageSize: PageSize
  onPageChange: (page: number) => void
  onPageSizeChange: (size: PageSize) => void
  isLoading: boolean
  isError: boolean
  onRetry: () => void
  selectedId: string | null
  onSelect: (id: string) => void
  getAnimationPhase: (id: string) => ResolveAnimationPhase
  lookback: string | null
  lookbackDate?: string | null
  onCloseLookback: () => void
}

const InboxList = ({
  status,
  kind,
  rating,
  q,
  onSearchChange,
  locationIds,
  items,
  totalCount,
  totalPages,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  isLoading,
  isError,
  onRetry,
  selectedId,
  onSelect,
  getAnimationPhase,
  lookback,
  lookbackDate,
  onCloseLookback,
}: InboxListProps) => {
  const [css] = useStyletron()
  const nav = useRovingListNav(items.length, index => {
    const item = items[index]
    if (item) onSelect(item.id)
  })

  // "Focus follows selection" (docs/11-chassis-plan.md PR 02 row): when the
  // selected item changes for a reason OTHER than clicking/keyboard-
  // selecting a row in THIS list (a deep link, browser Back/Forward, or the
  // resolve flow's auto-advance), move the roving tab stop — and DOM focus
  // — to match. A `selectedId` of `null` (nothing selected, e.g. on initial
  // load) intentionally does nothing here, per "no auto-select on load."
  useEffect(() => {
    if (!selectedId) return
    const index = items.findIndex(item => item.id === selectedId)
    if (index === -1) return
    nav.setActiveIndex(index)
    // `nav`'s identity changes every render (it's a fresh object from the
    // hook above); only `selectedId`/`items` should re-trigger this sync.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, items])

  // The rows pane is its own scroll container, so a new page, page size,
  // filter, or search would otherwise leave the new list scrolled to
  // wherever the previous one was. A filter or search change on page 1
  // keeps `page` at 1, so the filters are deps too. `locationIds` is a new
  // array on every URL change (a row select included), so it is keyed by
  // value.
  const rowsRef = useRef<HTMLDivElement>(null)
  const locationKey = locationIds.join(',')
  useEffect(() => {
    if (rowsRef.current) rowsRef.current.scrollTop = 0
  }, [page, pageSize, status, kind, rating, locationKey, q])

  const renderSkeleton = () =>
    Array.from({ length: SKELETON_ROW_COUNT }).map((_, index) => (
      <div
        key={`skeleton-${index}`}
        data-testid="feedback-list-skeleton-row"
        aria-hidden="true"
        className={css({
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '12px 16px',
          borderBottom: `1px solid ${palette.grey20}`,
        })}
      >
        <div
          className={css({
            width: '30px',
            height: '30px',
            borderRadius: '50%',
            backgroundColor: palette.grey20,
            flexShrink: 0,
          })}
        />
        <div
          className={css({
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
          })}
        >
          <div
            className={css({
              height: '12px',
              width: '60%',
              borderRadius: '4px',
              backgroundColor: palette.grey20,
            })}
          />
          <div
            className={css({
              height: '10px',
              width: '85%',
              borderRadius: '4px',
              backgroundColor: palette.grey20,
            })}
          />
        </div>
      </div>
    ))

  const renderError = () => (
    <div className={css({ padding: '32px 16px', textAlign: 'center' })}>
      <Text.BodyText3 tag="p" bold>
        {t('error_title')}
      </Text.BodyText3>
      <Button kind="secondary" onClick={onRetry}>
        {t('retry')}
      </Button>
    </div>
  )

  const renderEmpty = () => {
    const cause = emptyStateCause({ q, locationIds, status, kind })
    return (
      <div className={css({ padding: '32px 16px', textAlign: 'center' })}>
        <Text.BodyText3 tag="p" bold>
          {t(`empty.${cause}_title`)}
        </Text.BodyText3>
        <Text.BodyText4 tag="p" color="grey70">
          {t(`empty.${cause}_body`)}
        </Text.BodyText4>
        {cause === 'search' && (
          <Button kind="minimal" onClick={() => onSearchChange('')}>
            {t('clear_search')}
          </Button>
        )}
      </div>
    )
  }

  const renderRows = () =>
    items.map((item, index) => (
      <InboxListRow
        key={item.id}
        item={item}
        selected={item.id === selectedId}
        animationPhase={getAnimationPhase(item.id)}
        navProps={nav.getItemProps(index)}
      />
    ))

  // Early-return body states rather than a nested ternary (repo
  // convention): loading → skeletons, error → retry, no rows → empty-by-
  // cause copy, otherwise the real rows.
  const renderBody = () => {
    if (isLoading) return renderSkeleton()
    if (isError) return renderError()
    if (items.length === 0) return renderEmpty()
    return renderRows()
  }

  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        borderRight: `1px solid ${palette.grey20}`,
        backgroundColor: palette.white,
      })}
    >
      {lookback && (
        <LookbackBackHeader onBack={onCloseLookback} date={lookbackDate} />
      )}
      {/* Kept mounted (never unmounted) so the row list's scroll position
          survives a look-back round trip (docs/06-ux-rules.md "Look-back"). */}
      <div
        className={css({
          display: lookback ? 'none' : 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
        })}
      >
        <SearchHeader q={q} onSearchChange={onSearchChange} />
        <div
          {...nav.getListProps()}
          ref={rowsRef}
          aria-label={t('rows_aria')}
          className={css({ flex: 1, minHeight: 0, overflowY: 'auto' })}
        >
          {renderBody()}
        </div>
        <TablePaginationFooter
          page={page}
          pageSize={pageSize}
          totalCount={totalCount}
          totalPages={totalPages}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          layout="stacked"
        />
      </div>
    </div>
  )
}

export default InboxList
